// src/CheckoutPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "./contexts/CartContext";
import { useTheme } from "./contexts/ThemeContext";
import { useUser } from "./contexts/UserContext";
import logo from "./assets/logo.png";
import { createOrder } from "./firebase";
import shippingConfig from "./data/shippingConfig.json";
import ReCAPTCHA from "react-google-recaptcha";
import emailjs from "emailjs-com";

export default function CheckoutPage() {
  const { cart, total, clearCart } = useCart();
  const { darkMode, setDarkMode } = useTheme();
  const { userData } = useUser();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    address: "",
    mobile: "",
  });
  const [error, setError] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");

  useEffect(() => {
    if (userData) {
      setForm((prev) => ({
        ...prev,
        name: userData.name || "",
        email: userData.email || "",
      }));
    }
  }, [userData]);


  const isLoggedIn = !!userData;
  const isAdmin = userData?.role === "admin";


  const {
    shippingPerGrams: SHIPPING_PER_GRAMS,
    shippingCostPerUnit: SHIPPING_COST,
    enableGuestCaptcha: ENABLE_GUEST_CAPTCHA,
    disableAllOrders: DISABLE_ALL_ORDERS,
    disableGuestOrders: DISABLE_GUEST_ORDERS,
    emailJSServiceID,
    emailJSTemplateID,
    emailJSPublicKey,
    sendOrderEmail: SEND_ORDER_EMAIL
  } = shippingConfig;

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });


  const sendOrderEmail = async (order) => {
    try {
      const orders = order.items.map((i) => ({
        name: i.name,
        units: i.qty,
        price: i.price,
        image_url: i.img?.startsWith("http")
          ? i.img
          : `https://bubblebutter.in/${i.img}`
        //image_url: i.img || "",
      }));

      await emailjs.send(
        emailJSServiceID,
        emailJSTemplateID,
        {
          order_id: order.orderNumber,
          customer_name: order.customer.name,
          orders,
          cost: { shipping: order.shippingCharge, total: order.grandTotal },
          email: order.customer.email,
          logo_url: "https://yourdomain.com/assets/logo.png",
        },
        emailJSPublicKey
      );
      console.log("Order confirmation email sent!");
    } catch (err) {
      console.error("Failed to send order email:", err);
    }
  };

  const sendTelegramOrderMessage = async (order) => {
  try {
    const { telegramBotToken, telegramChatId, sendTelegramOrder } = shippingConfig;
    if (!sendTelegramOrder) return;

    const itemsList = order.items
      .map((i) => `• ${i.name} × ${i.qty} (₹${i.price})`)
      .join("\n");

    const message = `
────────────────────────
📦 *Order Received*       
🕒 ${order.orderPlacedTime}
────────────────────────
🧾 Order ID : *${order.orderNumber}*
👤 Name     : *${order.customer.name}*
📞 Mobile   : *${order.customer.mobile}*
📧 Email     : *${order.customer.email}*

🛍 *Items:*
${itemsList}

🚚 *Shipping      :* ₹${order.shippingCharge}
💰 *Grand Total :* ₹${order.grandTotal}
────────────────────────
    `;

    await fetch(
      `https://api.telegram.org/bot${telegramBotToken}/sendMessage`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          chat_id: telegramChatId,
          text: message,
          parse_mode: "Markdown"
        })
      }
    );
    console.log("📨 Telegram notification sent!");
  } catch (err) {
    console.error("Telegram notification failed:", err);
  }
};


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (DISABLE_ALL_ORDERS) {
      setError(
        "Orders are temporarily disabled. Please contact us on WhatsApp: +91-7767908975"
      );
      return;
    }

    if (!userData && DISABLE_GUEST_ORDERS) {
      setError(
        <div className="space-y-2">
          <p>Guest checkout is disabled temporarily. Please log in to place orders.</p>
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="px-4 py-2 bg-pink-600 text-white rounded hover:bg-pink-700"
          >
            Go to Login
          </button>
        </div>
      );
      return;
    }

    if (!form.name || !form.email || !form.address || !form.mobile) {
      setError("Please fill all fields, including mobile number");
      return;
    }

    if (!/^\d{10}$/.test(form.mobile)) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }

    if (!userData && ENABLE_GUEST_CAPTCHA && !captchaToken) {
      setError("Please complete the CAPTCHA to proceed.");
      return;
    }

    setLoading(true);

    try {
      const totalWeight = cart.reduce(
        (sum, item) => sum + (item.weight || 0) * (item.qty || 0),
        0
      );
      const shippingUnits = Math.ceil(totalWeight / SHIPPING_PER_GRAMS);
      const shippingCharge = shippingUnits * SHIPPING_COST;
      const grandTotal = total + shippingCharge;

      const order = {
        customer: form,
        items: cart.map((i) => ({
          id: i.id,
          name: i.name,
          price: i.price,
          qty: i.qty,
          weight: i.weight || 0,
          img: i.img || i.image || "",
        })),
        total,
        shippingCharge,
        grandTotal,
        captchaToken: captchaToken || null,
      };

      const { orderNumber } = await createOrder(order);
      order.orderNumber = orderNumber;

      // Capture order placed date/time
      const now = new Date();
      order.orderPlacedTime = now.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });


      if (SEND_ORDER_EMAIL) {
        await sendOrderEmail(order);
        
      }

      await sendTelegramOrderMessage(order);

      clearCart();
      navigate(`/success/${orderNumber}`);
    } catch (err) {
      console.error("Order Error:", err);
      setError("Failed to place order. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const shippingCharge = Math.ceil(
    cart.reduce((sum, item) => sum + (item.weight || 0) * (item.qty || 0), 0) /
      SHIPPING_PER_GRAMS
  ) * SHIPPING_COST;

  const grandTotal = total + shippingCharge;

  return (
    <div className={darkMode ? "dark" : ""}>
      <div className="min-h-screen bg-gradient-to-b from-white to-pink-50 dark:from-gray-900 dark:to-gray-800 text-gray-800 dark:text-gray-200 font-sans pb-20">

        {/* HEADER */}
        <header className="w-full h-16 md:h-20 shadow-sm bg-white/80 dark:bg-gray-900/80 backdrop-blur-md sticky top-0 z-50">
          <div className="max-w-6xl mx-auto flex items-center justify-between h-full px-4 gap-4">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
              <img src={logo} alt="Bubble Butter Logo" className="h-14 md:h-16 object-contain"/>
              <h1 className="text-xl md:text-2xl font-bold text-pink-600 dark:text-pink-300">
                Bubble Butter
              </h1>
            </div>

            {/* DESKTOP NAV */}
            <div className="hidden md:flex items-center gap-4">
              <button onClick={() => navigate("/")} className="hover:text-pink-600 dark:hover:text-pink-300">Home</button>
              <button onClick={() => navigate("/products")} className="hover:text-pink-600 dark:hover:text-pink-300">Products</button>
              <button
                onClick={() => navigate("/cart")}
                className="relative px-3 py-2 rounded-lg bg-pink-600 text-white shadow"
              >
                🛒
                {cart.length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-yellow-400 text-black text-xs px-2 rounded-full">{cart.length}</span>
                )}
              </button>
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700"
              >
                {darkMode ? "🌙" : "☀️"}
              </button>
            </div>

            {/* MOBILE NAV */}
            <div className="md:hidden flex items-center gap-3">
              <button
                onClick={() => navigate("/")}
                className="h-10 w-10 flex items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700 shadow-md hover:bg-gray-300 dark:hover:bg-gray-600 transition"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6 text-pink-600 dark:text-pink-300">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-10 0h10" />
                </svg>
              </button>
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 rounded-full bg-gray-200 dark:bg-gray-700"
              >
                {darkMode ? "🌙" : "☀️"}
              </button>
            </div>
          </div>
        </header>

        {/* CHECKOUT FORM */}
        <div className="max-w-2xl mx-auto p-4 md:p-6 pt-10">
          <h1 className="text-3xl font-extrabold mb-6 text-center">Checkout</h1>

          <form
            onSubmit={handleSubmit}
            className={`rounded-2xl shadow-xl p-6 md:p-8 transition ${
              darkMode ? "bg-gray-800" : "bg-white border border-pink-100"
            }`}
          >

            {["name", "email", "mobile"].map((field) => {
              const isEmail = field === "email";

              const isEmailReadOnly =
                isEmail &&
                isLoggedIn &&
                !isAdmin; // logged in but NOT admin

              return (
                <input
                  key={field}
                  name={field}
                  value={form[field]}
                  onChange={isEmailReadOnly ? undefined : handleChange}
                  readOnly={isEmailReadOnly}
                  type={field === "mobile" ? "tel" : "text"}
                  placeholder={
                    field === "mobile"
                      ? "Mobile Number"
                      : `Your ${field.charAt(0).toUpperCase() + field.slice(1)}`
                  }
                  className={`w-full p-3 mb-3 rounded-lg transition focus:outline-none focus:ring-2 focus:ring-pink-400 ${
                    darkMode
                      ? "bg-gray-700 text-white placeholder-gray-400"
                      : "bg-pink-50 border border-pink-200"
                  } ${
                    isEmailReadOnly ? "cursor-not-allowed opacity-80" : ""
                  }`}
                />
              );
            })}




            <textarea
              name="address"
              value={form.address}
              onChange={handleChange}
              placeholder="Shipping Address"
              className={`w-full p-3 mb-3 h-28 rounded-lg transition focus:outline-none focus:ring-2 focus:ring-pink-400 ${
                darkMode ? "bg-gray-700 text-white placeholder-gray-400" : "bg-pink-50 border border-pink-200"
              }`}
            />

            {!userData && ENABLE_GUEST_CAPTCHA && (
              <div className="mb-3">
                <ReCAPTCHA
                  sitekey={shippingConfig.recaptchaSiteKey}
                  onChange={(token) => setCaptchaToken(token)}
                />
              </div>
            )}

            {error && <div className="text-red-500 font-medium mb-3">{error}</div>}

            {/* SUBTOTAL / SHIPPING / GRAND TOTAL */}
            <div className="text-right text-lg font-semibold mb-4 space-y-1">
              <div className="flex justify-between px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <span>Subtotal:</span>
                <span>₹{total}</span>
              </div>
              <div className="flex justify-between px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <span>Shipping:</span>
                <span>₹{shippingCharge}</span>
              </div>
              <div className="flex justify-between px-3 py-1 bg-pink-100 dark:bg-pink-800 rounded-lg font-bold text-pink-600 dark:text-pink-300">
                <span>Grand Total:</span>
                <span>₹{grandTotal}</span>
              </div>
            </div>

            {/* PLACE ORDER BUTTON */}
            <div className="flex justify-end mt-4">
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-3 min-w-[180px] rounded-lg bg-gradient-to-r from-pink-600 to-pink-500 hover:from-pink-700 hover:to-pink-600 text-white font-semibold shadow-lg transition transform active:scale-95"
              >
                {loading ? "Placing..." : "Place Order"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
